/**
 * rowMetrics —— 「一行多高 / 某组是否可见 / 某组是否占添加入口」的**唯一判据源**。
 *
 * 【本文件为何存在】行高不是孤立计算：它由「各子数组的子项数」与「该组是否渲染添加入口」
 * 共同决定，而**同一批判据还被渲染层与校验层消费**。历史上这些判据在多处各写一份，
 * 任一处改动都会让「实际 DOM 高度」与「引擎算出的行高」漂移 —— 表现为行底凭空多/少 40px 空白，
 * 或 required 组在不可见行报错、用户被拦却定位到看不见的 cell。故收敛到本文件。
 *
 * 【同源消费方（改动本文件前必须逐个确认）】
 *  - 行高：`buildBedrockColumns` 的行高回调 / 内容总高估算 / 横向定位的未渲染行估算
 *  - 渲染：`renderCells`（子列 cell 的 `showAdd`）、`buildBedrockColumns`（各子列 render）
 *  - 校验：`useErrorLocator`（`findFirstDoc` 按子列规则与可见性遍历）
 *
 * 【零 React 依赖】本文件只有纯函数 + 模块级缓存，**不 import React**，可被 jest 直接测
 * （DOM 相关的 `pxRatio` 在测试内 stub `document` 即可）。
 */
import type { ChildBase, RowBase, SubArraySpec } from './types';

/** 每个子项行高（px，设计值）：Tailwind `h-[40px]` 转 rem、随根字号缩放 */
export const SUB_ROW = 40;

/**
 * 行间分隔线厚度（px）。
 *
 * 与 `getRowHeight` 的 `h * pxRatio() + N` 中的 N 是同一个语义量，故与行高同处
 * ── 三处（内容总高校验 / 虚拟滚动 `getRowHeight` / 横向定位的未渲染行估算）必须用同一个值。
 */
export const ROW_LINE_GAP = 2;

let cachedPxRatio = 0;

/**
 * 项目 rem 根字号（屏幕适配动态缩放）：首读缓存，避免 `getRowHeight` 大量重复读 DOM。
 *
 * ⚠️ 模块级缓存 → **单测里两次 stub 不同 `fontSize` 会拿到首次的缓存值**。
 * `rowMetrics` 的测试必须在 `beforeEach` 里 `jest.resetModules()`（或重新 import），
 * 否则跨断言污染会产生假绿。
 */
export const pxRatio = (): number => {
  if (!cachedPxRatio) cachedPxRatio = parseFloat(document.documentElement.style.fontSize) || 1;
  return cachedPxRatio;
};

/**
 * 行高缓存。**故意不导出**。
 *
 * 理由：它是「按签名缓存」的实现细节，唯一读写点是 `getRowHeightPx`。导出会让调用方
 * 绕过封装直接操作缓存（例如自行 `.set` 一个错的 key），破坏 `rowSignature` 的 key 契约。
 * 需要按行高做事的地方一律走 `rowHeightPx` / `getRowHeightPx`。
 */
const rowHeightCache = new Map<string, number>();

/**
 * 子表高度（单位：px）。
 *
 * - 有「+添加{emptyLabel}」行（`hasAddRow`）：子项数 + 1；空态也占 1 行，给添加按钮留位。
 * - 无添加行：只算子项数（查看态 / 拆单「已有执行周期」等 `SubArraySpec.disableAdd` 场景，
 *   渲染层已不输出该行，行高必须同步减掉，否则行底凭空多出 40px 空白）。
 *
 * 两种情况都至少保留 1 行：子项全空且无添加行时，仍需撑起主行高度（其余 doc/custom 列的内容）。
 */
export const sideHeight = (count: number, hasAddRow: boolean): number =>
  Math.max(count + (hasAddRow ? 1 : 0), 1) * SUB_ROW;

/**
 * 该子数组是否渲染「+添加{emptyLabel}」行。
 *
 * ⚠️ 渲染层（`renderCells` 的 `showAdd`）与行高计算（`sideHeight`）必须消费**同一个**判据：
 * 两处各写一份条件时，任一处改动都会让「实际 DOM 高度」与「引擎算出的行高」漂移，
 * 表现为行底多出/缺少 40px 空白。故收敛到本函数。
 */
export const subHasAddRow = <TRow, TChild>(sub: SubArraySpec<TRow, TChild>): boolean => !sub.disableAdd;

/**
 * 该行该子数组是否参与渲染（未声明 `SubArraySpec.visibleRow` 时恒可见）。
 *
 * ⚠️ 与 `subHasAddRow` 同理，本判据也必须**同源消费**：渲染（各 cell）、行高
 * （`computeRowHeight` / `computeRowSignature`）、校验（`findFirstDoc`）三处缺一不可 ——
 * 漏掉行高则该行被不可见组撑高留白；漏掉校验则 required 组在不可见行报错，
 * 用户被拦却定位不到（看不见那个 cell）。
 */
export const subRowVisible = <TRow, TChild>(sub: SubArraySpec<TRow, TChild>, row: TRow): boolean =>
  !sub.visibleRow || sub.visibleRow(row);

/**
 * 该行的高度（px）。取各**可见**子数组 `sideHeight` 的最大值，基线 `SUB_ROW`。
 *
 * 行级隐藏的组不参与撑高 —— 与渲染同源，否则该行底部会按一个看不见的组留白。
 */
export const computeRowHeight = <TRow extends RowBase, TChild extends ChildBase>(
  row: TRow,
  subArrays: SubArraySpec<TRow, TChild>[],
): number => {
  let max = SUB_ROW;
  for (const spec of subArrays) {
    if (!subRowVisible(spec, row)) continue;
    max = Math.max(max, sideHeight(spec.getItems(row).length, subHasAddRow(spec)));
  }
  return max;
};

/**
 * 行高缓存的 key。
 *
 * ⚠️ **拼接格式是缓存 key 的持久契约，不得改动**。格式变更本身无害（缓存全部失效），
 * 但若改动导致 **key 碰撞**（例如为了「简化」去掉 `${s.key}` 前缀），不同子数组结构的
 * 两行会互相命中 → 返回错误行高、**不报错**，且行在视口外时连截图都看不出来。
 *
 * 签名必须含两个维度：
 *  - 「有无添加行」（`a`/`n`）：`rowHeightCache` 是模块级共享的，若只在数量变化时换 key，
 *    查看态 ↔ 编辑态切换（`disableAdd` 翻转）会命中切换前多算 1 行的旧高度；
 *  - 「是否行级可见」：不可见组标记 `x`（与 `count:flag` 形态不重叠，不会撞 key）。
 */
export const computeRowSignature = <TRow extends RowBase, TChild extends ChildBase>(
  row: TRow,
  subArrays: SubArraySpec<TRow, TChild>[],
): string => {
  const parts = subArrays.map((s) => {
    if (!subRowVisible(s, row)) return `${s.key}:x`;
    return `${s.key}:${s.getItems(row).length}:${subHasAddRow(s) ? 'a' : 'n'}`;
  });
  return `${row.id}|${parts.join('|')}`;
};

/**
 * 该行渲染高度（px，含 rem 缩放与行间分隔线）。
 *
 * **不走缓存**。用于「内容总高」这类需要按源码语义逐个求值的场合。
 */
export const rowHeightPx = <TRow extends RowBase, TChild extends ChildBase>(
  row: TRow,
  subArrays: SubArraySpec<TRow, TChild>[],
): number => computeRowHeight(row, subArrays) * pxRatio() + ROW_LINE_GAP;

/**
 * 该行渲染高度（px，**带缓存**）。虚拟滚动的 `getRowHeight` 走这里。
 *
 * 缓存命中判据是 `rowSignature`；未命中则计算并回写。两条路径的返回值同基准
 * （`computeRowHeight * pxRatio() + ROW_LINE_GAP`），故命中与未命中结果恒等。
 */
export const getRowHeightPx = <TRow extends RowBase, TChild extends ChildBase>(
  row: TRow,
  subArrays: SubArraySpec<TRow, TChild>[],
): number => {
  const key = computeRowSignature(row, subArrays);
  const cached = rowHeightCache.get(key);
  if (cached !== undefined) return cached * pxRatio() + ROW_LINE_GAP;
  const h = computeRowHeight(row, subArrays);
  rowHeightCache.set(key, h);
  return h * pxRatio() + ROW_LINE_GAP;
};
